import { Button } from "@sgi/gravity/Button";
import { PasswordInput, PasswordRequirementProps, PasswordRequirementStatuses } from "@sgi/gravity/PasswordInput";
import { Typography } from "@sgi/gravity/Typography";
import React, { useEffect, useState } from "react";
import { Box, VStack } from "@chakra-ui/react";
import { useWrapper } from "../Context/WrapperContext";
import { getSettings } from "../getSettings";

const EMAIL_EXISTS_MESSAGE =
  "A user with the specified ID already exists. Please choose a different one.";

// Requirement:
// 1. Do real-time validation for password, but do not display cross-sign until user change focus
//    If user comes back to fix password then do real-time validation including failed rules
// 2. Display 'Password doesn't meet the following requirements' after user change focus

export default function SetPassword({
  description,
  password,
  setPassword,
  retypePassword,
  setRetypePassword,
  onContinue,
  onCancel,
  pageLevelError,
  disableContinue,
  termsElement,
  continueButtonName,
}: {
  readonly description: string;
  readonly password: string;
  readonly setPassword: React.Dispatch<React.SetStateAction<string>>;
  readonly retypePassword: string;
  readonly setRetypePassword: React.Dispatch<React.SetStateAction<string>>;
  readonly onContinue: () => void;
  readonly onCancel: () => void;
  readonly pageLevelError: string;
  readonly disableContinue: boolean;
  readonly termsElement?: React.ReactElement;
  readonly continueButtonName: string;
}) {
  const [isFirstPasswordTry, setIsFirstPasswordTry] = useState(true);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [passwordInputError, setPasswordInputError] = useState("");
  const [confirmPasswordInputError, setConfirmPasswordInputError] =
    useState("");

  const [lengthStatus, setLengthStatus] = useState<
    PasswordRequirementStatuses | undefined
  >("default");
  const [upperCaseStatus, setUpperCaseStatus] = useState<
    PasswordRequirementStatuses | undefined
  >("default");
  const [lowerCaseStatus, setLowerCaseStatus] = useState<
    PasswordRequirementStatuses | undefined
  >("default");
  const [numberStatus, setNumberStatus] = useState<
    PasswordRequirementStatuses | undefined
  >("default");

  const requirements: PasswordRequirementProps[] = [
    {
      requirement: "Be 8 to 64 characters in length",
      status: lengthStatus,
    },
    {
      requirement: "Include at least one upper case letter (A-Z)",
      status: upperCaseStatus,
    },
    {
      requirement: "Include at least one lower case letter (a-z)",
      status: lowerCaseStatus,
    },
    {
      requirement: "Include at least one number (0-9)",
      status: numberStatus,
    },
  ];

  const { decrement } = useWrapper();

  useEffect(() => {
    if (pageLevelError === EMAIL_EXISTS_MESSAGE) {
      decrement();
    }
  }, [decrement, pageLevelError]);

  useEffect(() => {
    if (retypePassword === "" || retypePassword === password) {
      setConfirmPasswordInputError("");
    } else {
      setConfirmPasswordInputError("Passwords do not match.");
    }
  }, [password, retypePassword]);

  useEffect(() => {
    if (
      lengthStatus === "success" &&
      upperCaseStatus === "success" &&
      lowerCaseStatus === "success" &&
      numberStatus === "success"
    ) {
      setIsPasswordValid(true);
    } else {
      setIsPasswordValid(false);
    }

    checkToDisplayPasswordError();
  }, [
    lengthStatus,
    upperCaseStatus,
    lowerCaseStatus,
    numberStatus,
    checkToDisplayPasswordError,
  ]);

  useEffect(() => {
    checkValueForLength(password);
    checkValueForUpperCase(password);
    checkValueForLowerCase(password);
    checkValueForNumber(password);
    checkToDisplayPasswordError();
  }, [
    password,
    isFirstPasswordTry,
    isPasswordValid,
    checkValueForLength,
    checkValueForUpperCase,
    checkValueForLowerCase,
    checkValueForNumber,
    checkToDisplayPasswordError,
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  function checkValueForLength(e: any) {
    if (e !== undefined && e !== null && e !== "") {
      if (/^.{8,64}$/.test(e)) {
        setLengthStatus("success");
      } else if (!isFirstPasswordTry) {
        setLengthStatus("error");
      } else {
        setLengthStatus("default");
      }
    } else {
      setLengthStatus("default");
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  function checkValueForUpperCase(e: any) {
    if (e !== undefined && e !== null && e !== "") {
      if (/[A-Z]/.test(e)) {
        setUpperCaseStatus("success");
      } else if (!isFirstPasswordTry) {
        setUpperCaseStatus("error");
      } else {
        setUpperCaseStatus("default");
      }
    } else {
      setUpperCaseStatus("default");
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  function checkValueForLowerCase(e: any) {
    if (e !== undefined && e !== null && e !== "") {
      if (/[a-z]/.test(e)) {
        setLowerCaseStatus("success");
      } else if (!isFirstPasswordTry) {
        setLowerCaseStatus("error");
      } else {
        setLowerCaseStatus("default");
      }
    } else {
      setLowerCaseStatus("default");
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  function checkValueForNumber(e: any) {
    if (e !== undefined && e !== null && e !== "") {
      if (/\d/.test(e)) {
        setNumberStatus("success");
      } else if (!isFirstPasswordTry) {
        setNumberStatus("error");
      } else {
        setNumberStatus("default");
      }
    } else {
      setNumberStatus("default");
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  function checkToDisplayPasswordError() {
    // do not display error message if password is valid or empty
    if (
      isPasswordValid ||
      (lengthStatus === "default" &&
        upperCaseStatus === "default" &&
        lowerCaseStatus === "default" &&
        numberStatus === "default") ||
      password === ""
    ) {
      setPasswordInputError("");
    } else if (password !== "" && !isFirstPasswordTry) {
      setPasswordInputError(
        "Password doesn't meet the following requirements"
      );
    }
  }

  function handleClick() {
    checkToDisplayPasswordError();
    onContinue();
  }

  const settings = getSettings();

  function handleCancelClick() {
    if (settings.isSignInPolicy) {
      onCancel();
      return;
    }
    setPassword("");
    setRetypePassword("");
    // reset validation bullets
    setLengthStatus("default");
    setUpperCaseStatus("default");
    setLowerCaseStatus("default");
    setNumberStatus("default");

    setIsFirstPasswordTry(true);
    onCancel();
    window.location.reload();
  }

  return (
    <Box
      style={{ zIndex: "200", position: "relative" }}
      data-testid="password-test"
    >
      <Typography variant="body" mb={8}>{description}</Typography>
      <PasswordInput
        name="password"
        placeholder="Enter your password"
        id="password-input"
        label="Password"
        data-testid="password-input"
        isRequired={true}
        value={password}
        autoComplete="new-password"
        maxLength={64}
        onChange={(e: any) => {
          setPassword(e.target.value);
        }}
        onBlur={() => {
          setIsFirstPasswordTry(false);
        }}
        onKeyDown={(e: any) => {
          if (e.key === "Enter") handleClick();
        }}
        error={passwordInputError}
        passwordRequirements={requirements}
      />
      <br />
      <PasswordInput
        name="CheckPassword"
        label="Confirm Password"
        id="password-verify"
        placeholder="Re-enter your password"
        style={{ backgroundColor: "white" }}
        onChange={(e: any) => setRetypePassword(e.target.value)}
        onKeyDown={(e: any) => {
          if (e.key === "Enter") handleClick();
        }}
        error={confirmPasswordInputError}
        data-testid="password-verify"
        value={retypePassword}
        autoComplete="new-password"
        maxLength={64}
        isRequired={true}
      />
      <br />
      {termsElement}
      <br />
      <VStack mt={6}>
        <Button
          isFullWidth
          onClick={handleClick}
          data-testid="password-continue-button"
          isDisabled={disableContinue}
        >
          {continueButtonName}
        </Button>
        <Button
          isFullWidth
          variant="ghost"
          style={{ marginTop: "20px", marginBottom:"80px" }}
          onClick={handleCancelClick}
          data-testid="password-cancel-button"
        >
          Cancel
        </Button>
      </VStack>
    </Box>
  );
}
